import React from "react";
import { motion } from "framer-motion";
import { Instagram, Linkedin } from "lucide-react";

// Per-founder crop tuning. Both photo containers share the exact same box
// (aspect-[4/5]) and use object-fit: cover, so nothing is stretched. We only
// zoom / re-position each image individually to bring both faces to roughly
// the same vertical level as a matched waist-up portrait.
const CROP_CONFIG = {
  "Shubhi Jain": {
    // Original photo is a full-body standing shot — scale in on the upper body.
    objectPosition: "50% 18%",
    transform: "scale(1.55)",
    transformOrigin: "50% 22%",
  },
  "Shubh Singh": {
    // Original photo is already a close waist-up shot — light zoom + subtle
    // reposition so his face lands at the same height as Shubhi's.
    objectPosition: "50% 32%",
    transform: "scale(1.08)",
    transformOrigin: "50% 30%",
  },
};
const cropFor = (name) => ({
  objectPosition: "50% 25%",
  ...(CROP_CONFIG[name] || {}),
});

export default function Founders({ founders }) {
  const list = (founders || []).sort((a, b) => (a.order || 0) - (b.order || 0));
  return (
    <section id="founders" className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <div className="text-xs tracking-[0.28em] uppercase text-slate-500">Founders</div>
          <h2 className="mt-3 font-display text-3xl sm:text-5xl leading-tight text-[#0B132B]">The People Behind Be Hire Ready</h2>
        </div>
        <div className="mt-14 grid gap-8 lg:grid-cols-2 items-stretch">
          {list.map((f, i) => (
            <motion.article key={f.id || f.name}
                            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                            className="relative rounded-3xl bg-gradient-to-br from-slate-50 to-white ring-1 ring-slate-100 overflow-hidden">
              <div className="grid sm:grid-cols-5">
                <div className="sm:col-span-2 relative aspect-[4/5] bg-slate-100 overflow-hidden">
                  {f.photo_url ? (
                    <img src={f.photo_url} alt={`${f.name} — ${f.designation}`}
                         loading="lazy" decoding="async"
                         className="absolute inset-0 w-full h-full object-cover"
                         style={cropFor(f.name)} />
                  ) : (
                    <div className="absolute inset-0 grid place-items-center text-slate-400 text-xs">Photo</div>
                  )}
                  <div className="absolute bottom-3 left-3 flex gap-2">
                    {f.linkedin_url && <a href={f.linkedin_url} aria-label={`${f.name} on LinkedIn`} className="h-8 w-8 rounded-full glass-light grid place-items-center"><Linkedin size={13} /></a>}
                    {f.instagram_url && <a href={f.instagram_url} aria-label={`${f.name} on Instagram`} className="h-8 w-8 rounded-full glass-light grid place-items-center"><Instagram size={13} /></a>}
                  </div>
                </div>
                <div className="sm:col-span-3 p-6 sm:p-8">
                  <div className="text-[11px] tracking-[0.28em] uppercase text-slate-500">{f.designation}</div>
                  <h3 className="mt-2 font-display text-2xl sm:text-3xl text-[#0B132B]">{f.name}</h3>
                  <p className="mt-4 text-sm sm:text-[15px] text-slate-600 leading-relaxed">{f.biography}</p>
                  {(f.expertise || []).length > 0 && (
                    <div className="mt-5 flex flex-wrap gap-2">
                      {f.expertise.map((tag) => (
                        <span key={tag} className="px-3 py-1 rounded-full text-[11px] tracking-wide bg-[#0B132B] text-white/90">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
